import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useApi } from "../../hooks/useApi";
import { Badge } from "../../components/ui/badge";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../../components/ui/select";

const CATEGORIES = ["auth", "admin", "account"];

export default function AdminSecurityLog() {
  const api = useApi();
  const [category, setCategory] = useState("all");
  const [outcome, setOutcome] = useState("all");
  const [page, setPage] = useState(1);

  const { data, isLoading } = useQuery({
    queryKey: ["admin-security-events", category, outcome, page],
    queryFn: async () => (await api.get("/admin/security-events", {
      params: { page, page_size: 50, category: category === "all" ? "" : category, outcome: outcome === "all" ? "" : outcome },
    })).data,
  });

  const totalPages = data ? Math.max(1, Math.ceil(data.total / data.page_size)) : 1;

  return (
    <div data-testid="admin-security-log-page">
      <h1 className="font-display text-display-xs mb-2">Security Activity Log</h1>
      <p className="text-white/50 text-xs mb-6">Sign-ins, password resets, TOTP, administrative actions and authorization failures. Retained 400 days, insert-only.</p>
      <div className="flex flex-wrap gap-3 mb-6">
        <Select value={category} onValueChange={(v) => { setCategory(v); setPage(1); }}>
          <SelectTrigger data-testid="admin-security-category-filter" className="w-40 bg-transparent border-white/20"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Any category</SelectItem>
            {CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={outcome} onValueChange={(v) => { setOutcome(v); setPage(1); }}>
          <SelectTrigger data-testid="admin-security-outcome-filter" className="w-40 bg-transparent border-white/20"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Any outcome</SelectItem>
            <SelectItem value="success">Success</SelectItem>
            <SelectItem value="failure">Failure</SelectItem>
            <SelectItem value="requested">Requested</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {isLoading && <p className="text-white/60 text-sm">Loading…</p>}
      <div className="divide-y divide-white/10 border-t border-b border-white/10" data-testid="admin-security-events-table">
        {data?.items?.map((ev) => (
          <div key={ev.id} data-testid={`admin-security-event-${ev.id}`} className="py-3 flex items-center justify-between gap-3 text-sm flex-wrap">
            <div>
              <span className="capitalize">{ev.event_type.replace(/_/g, " ")}</span>
              <p className="text-white/50 text-xs mt-0.5">{ev.email || ev.account_id || "—"}{ev.ip ? ` · ${ev.ip}` : ""}</p>
            </div>
            <span className="flex items-center gap-2">
              <Badge
                className={
                  ev.outcome === "failure" ? "bg-red-500/15 text-red-300 border-red-500/40" :
                  ev.outcome === "success" ? "bg-green-500/10 text-green-300 border-green-500/30" :
                  "bg-white/5 text-white/60 border-white/20"
                }
              >
                {ev.outcome}
              </Badge>
              <span className="text-white/40 text-xs">{new Date(ev.created_at).toLocaleString()}</span>
            </span>
          </div>
        ))}
        {data && data.items.length === 0 && <p data-testid="admin-security-events-empty" className="text-white/50 text-sm py-6">No events match these filters.</p>}
      </div>

      {data && totalPages > 1 && (
        <div className="flex items-center gap-4 mt-6 text-xs text-white/60">
          <button data-testid="admin-security-prev-page" disabled={page <= 1} onClick={() => setPage((p) => p - 1)} className="underline disabled:opacity-30">Previous</button>
          <span>Page {page} of {totalPages}</span>
          <button data-testid="admin-security-next-page" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)} className="underline disabled:opacity-30">Next</button>
        </div>
      )}
    </div>
  );
}
